/**
 * Sticky Zone - Frontend
 *
 * Calculates and sets the `--sticky-top` CSS variable on each sticky item so
 * that multiple items stack flush against each other at the top of the viewport
 * as the user scrolls. A ResizeObserver keeps the values in sync if any item's
 * height changes (e.g. on window resize or dynamic content).
 *
 * The zone itself acts as the scroll boundary — sticky items stop sticking once
 * the bottom of the zone scrolls past their position.
 */

const ZONE_SELECTOR = '.wp-block-amp-child-theme-sticky-zone';
const ITEM_SELECTOR = '.wp-block-amp-child-theme-sticky-item';

// Only items whose nearest zone ancestor is this zone — excludes items that
// belong to a nested zone so each zone manages its own items independently.
function getOwnItems(zone) {
	return Array.from(zone.querySelectorAll(ITEM_SELECTOR)).filter(
		(item) => item.closest(ZONE_SELECTOR) === zone
	);
}

function updateScrollPadding() {
	let total = 0;
	document.querySelectorAll(ZONE_SELECTOR).forEach((zone) => {
		getOwnItems(zone).forEach((item) => {
			total += item.offsetHeight;
		});
	});
	document.documentElement.style.scrollPaddingTop = `calc(${total}px + 1rem)`;
}

function updateStickyTops(zone) {
	const items = getOwnItems(zone);
	const count = items.length;
	let top = 0;
	items.forEach((item, index) => {
		item.style.setProperty('--sticky-top', `${top}px`);
		// Earlier items must paint over later ones so the first item isn't
		// visually swallowed as the second item slides up beneath it.
		item.style.zIndex = count - index;
		top += item.offsetHeight;
	});
	updateScrollPadding();
}

function isVisible(el) {
	return el.getClientRects().length > 0;
}

function handleAnchorClicks(zone) {
	zone.addEventListener('click', (e) => {
		const link = e.target.closest('a[href^="#"]');
		if (!link) return;

		const hash = link.getAttribute('href').slice(1);
		if (!hash) return;

		const primaryTarget = document.getElementById(hash);
		if (!primaryTarget || isVisible(primaryTarget)) return;

		const visibleTarget = Array.from(
			document.querySelectorAll(`[id="${CSS.escape(hash)}"]`)
		).find(isVisible);

		if (!visibleTarget) return;

		e.preventDefault();
		history.pushState(null, '', `#${hash}`);
		visibleTarget.scrollIntoView({ block: 'start' });
	});
}

function initStickyZones() {
	document.querySelectorAll(ZONE_SELECTOR).forEach((zone) => {
		const items = getOwnItems(zone);
		if (!items.length) return;

		updateStickyTops(zone);
		handleAnchorClicks(zone);

		new ResizeObserver(() => updateStickyTops(zone)).observe(zone);
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initStickyZones);
} else {
	initStickyZones();
}
